import {
  keepPreviousData,
  useInfiniteQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import {
  getScreens,
  type IScreensReadParams,
  type ScreensReadModel,
} from 'shared/src/api';
import { useFilterListAtom } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/useFilterListAtom';
import { filterListStore } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/FilterListAtomProvider';
import { filterListStateAtom } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/filterList.atom';
import { applyDeferredViewEventSelect } from './utils/applyDeferredViewEventSelect';

type SearchResultQueryParams = {
  screenName: string;
  query: string;
  nextToken?: string;
};

/**
 * 검색 결과 전용 무한 스크롤 쿼리 훅
 * - query 필수 (빈 문자열이면 비활성화)
 * - 검색어/필터 변경 시 항상 새로 요청 (staleTime: 0)
 * - select 파생 로직(VIEW 이벤트 지연 발화)은 applyDeferredViewEventSelect 참조
 */
export const useSearchResultQuery = ({
  screenName,
  query,
  nextToken,
}: SearchResultQueryParams) => {
  const {
    filterListAtomValue: { screen_name: _ignoredScreenName, from_screen: _ignoredFromScreen, ...restFilterListAtomValue },
  } = useFilterListAtom();

  const queryResult = useInfiniteQuery({
    queryKey: [
      'searchResult',
      { ...restFilterListAtomValue, nextToken, screenName, query },
    ],
    queryFn: ({ pageParam }) => {
      const requestParams = {
        ...restFilterListAtomValue,
        next_token: pageParam ?? null,
        screen: screenName,
        query,
      } as IScreensReadParams;
      return getScreens(requestParams);
    },
    enabled: query !== '',
    initialPageParam: nextToken ?? null,
    getNextPageParam: lastPage => {
      return lastPage.nextToken || undefined;
    },
    select: applyDeferredViewEventSelect,
    staleTime: 0,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });

  return queryResult;
};

/**
 * SearchAnalyticsProvider getDefaults에서 사용하는 캐시 lookup 헬퍼.
 * useSearchResultQuery와 동일한 queryKey 구조로 raw InfiniteData를 조회한다.
 * - select 적용 전 데이터이므로 data.pages[last].logging을 그대로 추출 가능
 * - ScreensAnalyticsProvider의 getScreensInfiniteQueryData와 1:1 대응
 * - 캐시 미스 시 undefined를 반환해 AnalyticsScreen의 SCREEN-only fallback이 동작
 */
export const getSearchResultInfiniteQueryData = (
  queryClient: QueryClient,
  params: {
    screenName: string;
    query: string;
    nextToken?: string;
    instanceId: string;
  },
): InfiniteData<ScreensReadModel> | undefined => {
  const atomMap = filterListStore.get(filterListStateAtom);
  const atomValue = atomMap[params.instanceId] ?? {};
  const {
    screen_name: _ignoredScreenName,
    from_screen: _ignoredFromScreen,
    ...restFilterListAtomValue
  } = atomValue;
  return queryClient.getQueryData<InfiniteData<ScreensReadModel>>([
    'searchResult',
    {
      ...restFilterListAtomValue,
      nextToken: params.nextToken,
      screenName: params.screenName,
      query: params.query,
    },
  ]);
};
